// Browser-side Twitch chat client — anonymous IRC-over-WebSocket, no auth, no
// server. A direct TS port of the homelab service's twitch.rs so a Twitch overlay
// renders identically whether it runs client-side (this) or through the service
// (Kick/YouTube/TikTok path). Emits the same OverlayBaseMessage wire shape.

import { applyEmotes, EMOTE_HOLD_MS, type EmoteMap } from './emoteMap';
import { acquireRoomEmotes, type RoomEmotes } from './roomEmotes';
import { createRepairer, type SegmentUpdate } from './emoteRepair';
import { personalEmotesFor } from './seventvLive';
import type { SeventvEmoteChange } from './overlayConfig';
import { applyLinks } from './linkDetect';
import { loadCheermotes, applyCheermotes, type CheermoteMap } from './cheermotes';
import type { MessageSegment, OverlayBadge, OverlayBaseMessage } from './overlayMessage';
import { resolveNameColor } from './defaultNameColor';
import { connectChannelPoints, type Redemption } from './channelPoints';
import { isFromOtherRoom } from './twitchBadgeRooms';

const IRC_WS = 'wss://irc-ws.chat.twitch.tv:443';
const HISTORY_URL = 'https://recent-messages.robotty.de/api/v2/recent-messages';
/** Enough recent chat to fill the canvas on start, never a wall of it. */
const HISTORY_LIMIT = 40;
/** Live chat waits at most this long for history, so a slow history service
 *  delays the first messages briefly instead of holding chat. */
const HISTORY_WAIT_MS = 3000;
/** A socket that sent nothing this long is asked for a PONG (the app's chat
 *  pings after the same 15 s). */
const KEEPALIVE_IDLE_MS = 15_000;
/** No reply this long after that PING: the socket is dead (a dropped network
 *  can leave it open without ever closing), so a fresh one takes over. Measured
 *  from the PING, not from the last data, so a hidden tab whose timers run once
 *  a minute never mistakes a quiet chat for a dead one. */
const KEEPALIVE_PONG_MS = 10_000;
const KEEPALIVE_CHECK_MS = 5_000;
/** Recent chat asked for after a reconnect; anything older in the gap would
 *  already have scrolled off. */
const GAP_LIMIT = 100;
/** Ids of the rows this channel showed last, for skipping repeats. */
const RECENT_IDS_MAX = 1_000;
/** Twitch's maintenance notice, exactly: never a chat line that says it. */
const RECONNECT_LINE = /^(@\S+ )?:tmi\.twitch\.tv RECONNECT$/;

/** What a moderator can ask of the overlay from chat. */
export type OverlayCommand = 'refresh' | 'clear';

const OVERLAY_COMMANDS: Record<string, OverlayCommand> = {
  '!refreshoverlay': 'refresh',
  '!clearoverlay': 'clear',
};

/** How far a command's send time may trail the page's start and still count.
 *  Live IRC never re-delivers a line, so this only guards clock skew. */
const COMMAND_SKEW_MS = 120_000;

/**
 * The overlay command a chat line carries, or null.
 *
 * Only the broadcaster and moderators, only a line whose whole text is the
 * command, and never a line from chat history: a replayed `!refreshoverlay`
 * would reload the page, which replays history again, forever. History lines
 * carry `historical=1`; the send-time check is a second, independent guard.
 */
export function overlayCommandOf(tags: Record<string, string>, text: string, pageStartedAt: number): OverlayCommand | null {
  const cmd = OVERLAY_COMMANDS[text.trim().toLowerCase()];
  if (!cmd) return null;
  if (tags['historical'] === '1' || tags['rm-received-ts']) return null;
  const badges = `,${tags['badges'] ?? ''}`;
  const privileged = tags['mod'] === '1' || badges.includes(',broadcaster/') || badges.includes(',moderator/');
  if (!privileged) return null;
  const sent = Number(tags['tmi-sent-ts']);
  if (!Number.isFinite(sent) || sent < pageStartedAt - COMMAND_SKEW_MS) return null;
  return cmd;
}

export interface TwitchOptions {
  /** Fill the overlay with the channel's recent chat on start. */
  history?: boolean;
  /** Whether to obey !refreshoverlay and !clearoverlay from the broadcaster and
   *  moderators, read per line so the setting applies without reconnecting. */
  commands?: () => boolean;
}

const PAGE_STARTED_AT = Date.now();

/** Shared Chat partner tables held at once (a session is two to six channels). */
const MAX_PARTNER_ROOMS = 16;

interface HeldLine {
  line: string;
  fromHistory: boolean;
  id: string;
  userId: string;
}

interface Partner {
  room: RoomEmotes;
  /** Its own table is usable (loaded, or saved from last time). */
  loaded: boolean;
  /** Its rows waiting for that table, the first time it is seen. */
  held: HeldLine[] | null;
}

export interface TwitchHandlers {
  onMessage: (msg: OverlayBaseMessage) => void;
  /** CLEARMSG — a single deleted message id. */
  onDelete?: (msgId: string) => void;
  /** CLEARCHAT with a target — timeout/ban; drop this user's messages. */
  onClearUser?: (userId: string) => void;
  /** CLEARCHAT with no target — full chat clear. */
  onClearChat?: () => void;
  /** A moderator's overlay command (see overlayCommandOf). */
  onCommand?: (cmd: OverlayCommand) => void;
  /** The channel's 7TV emote set changed (emotes added, removed or renamed). */
  onEmoteSetChange?: (changes: SeventvEmoteChange[], roomId: string) => void;
  /** Rows already shown whose emotes were mended (see emoteRepair), by id. */
  onUpdate?: (updates: SegmentUpdate[]) => void;
}

/** Connect one Twitch channel. Returns a disposer that stops reconnecting + closes. */
export function connectTwitch(channel: string, handlers: TwitchHandlers, options: TwitchOptions = {}): () => void {
  const login = channel.replace(/^#/, '').toLowerCase();
  // Recent chat plays first, in order: live lines that land while it loads wait
  // behind it (briefly), so history never interleaves with or follows live chat.
  let historyPending = !!options.history;
  const heldLive: string[] = [];
  let ws: WebSocket | null = null;
  let disposed = false;
  let retry = 1000;
  // Liveness: when the socket last sent anything, and when we last asked it to.
  let lastRx = 0;
  let pingSentAt = 0;
  let connectedOnce = false;
  // The newest row shown (Twitch's send time) and the ids shown lately, so a
  // reconnect asks history for exactly what it missed.
  let lastSentTs = 0;
  const recentIds = new Set<string>();
  let emotes: EmoteMap | null = null;
  let homeRoom = '';
  let home: RoomEmotes | null = null;
  // Shared Chat partners' tables, oldest first. A partner row uses its own
  // channel's table once that has loaded, and the home table until then.
  const partners = new Map<string, Partner>();
  const repairer = createRepairer((updates) => {
    if (!disposed) handlers.onUpdate?.(updates);
  });
  let cheermotes: CheermoteMap | null = null;
  let cheerLoadStarted = false;
  let cheerLoadAttempts = 0;
  let stopPoints: (() => void) | null = null;
  // The points feed names the redeemer but carries no colour or badges, so keep what
  // chat last showed for each chatter. Capped, oldest first out.
  const chatters = new Map<string, { color: string; badges: OverlayBadge[] }>();

  const rememberChatter = (msg: OverlayBaseMessage) => {
    // A shared-chat row wears the badges of the channel it was sent in, which
    // are not the chatter's badges here.
    if (!msg.user_id || !msg.color || isFromOtherRoom(msg.tags)) return;
    chatters.delete(msg.user_id);
    chatters.set(msg.user_id, { color: msg.color, badges: msg.badges });
    if (chatters.size > 1000) chatters.delete(chatters.keys().next().value as string);
  };

  const maybeListenPoints = (roomId?: string) => {
    if (stopPoints || !roomId || disposed) return;
    stopPoints = connectChannelPoints(roomId, (r) => {
      if (!disposed) handlers.onMessage(buildRedemption(r, roomId, login, chatters.get(r.userId)));
    });
  };

  // Lines wait here, in order, while a table nothing was saved for loads (a
  // first visit, a new Shared Chat partner), at most EMOTE_HOLD_MS, so rows
  // show with their emotes instead of changing a moment after they appear.
  // A channel with saved lists never waits.
  const gated: [string, boolean][] = [];
  let gateTimer: ReturnType<typeof setTimeout> | null = null;
  let gateSeq = 0;
  const openGate = (seq: number) => {
    if (seq !== gateSeq || !gateTimer) return;
    clearTimeout(gateTimer);
    gateTimer = null;
    while (!disposed && !gateTimer && gated.length > 0) {
      const [line, fromHistory] = gated.shift()!;
      handleLine(line, fromHistory, true);
    }
  };
  const closeGate = (ready: Promise<unknown>) => {
    const seq = ++gateSeq;
    gateTimer = setTimeout(() => openGate(seq), EMOTE_HOLD_MS);
    void ready.then(() => openGate(seq));
  };

  const maybeLoadEmotes = (roomId?: string) => {
    if (home || !roomId || disposed) return;
    // Drawn from the copies saved last time at once, each list replaced as its
    // provider answers (and asked again while it is down); the rows drawn
    // before are repaired. The table also keeps the channel's 7TV set live and
    // brings in chatters' personal sets. Closing the channel lets it go.
    homeRoom = roomId;
    home = acquireRoomEmotes('TWITCH', roomId, {
      onSetChange: (changes) => {
        if (!disposed) handlers.onEmoteSetChange?.(changes, roomId);
      },
    });
    emotes = home.map;
    if (!home.map.warm) closeGate(home.ready);
  };

  /** A Shared Chat partner's table, loaded on its first row. */
  const partnerTable = (roomId: string) => {
    const held = partners.get(roomId);
    if (held) {
      partners.delete(roomId);
      partners.set(roomId, held);
      return held;
    }
    const room = acquireRoomEmotes('TWITCH', roomId, { persist: 'spare' });
    const entry: Partner = { room, loaded: !!room.map.warm, held: null };
    partners.set(roomId, entry);
    if (partners.size > MAX_PARTNER_ROOMS) {
      const [oldest, gone] = partners.entries().next().value as [string, { room: RoomEmotes }];
      partners.delete(oldest);
      gone.room.release();
    }
    void entry.room.ready.then(() => {
      if (disposed || partners.get(roomId) !== entry) return;
      entry.loaded = true;
      releasePartner(entry);
      // Rows drawn before (with the home table, or a saved copy) are mended.
      repairer.heal(roomId);
    });
    if (!entry.loaded) {
      // Only this partner's rows wait for its table, in their own order, so the
      // rest of chat never stalls; past EMOTE_HOLD_MS they show and are mended.
      entry.held = [];
      setTimeout(() => releasePartner(entry), EMOTE_HOLD_MS);
    }
    return entry;
  };

  const releasePartner = (entry: Partner) => {
    const lines = entry.held;
    if (!lines) return;
    entry.held = null;
    for (const h of lines) handleLine(h.line, h.fromHistory);
  };

  /** Moderation reaches rows still waiting for their partner's table. */
  const dropHeld = (match: (h: HeldLine) => boolean) => {
    for (const p of partners.values()) if (p.held) p.held = p.held.filter((h) => !match(h));
  };

  const maybeLoadCheermotes = (roomId?: string) => {
    if (cheerLoadStarted || !roomId) return;
    cheerLoadStarted = true;
    cheerLoadAttempts += 1;
    void loadCheermotes(roomId).then((m) => {
      cheermotes = m;
      // loadCheermotes swallows every failure into {}, and a successful response always
      // carries Twitch's globals — so an empty map means the fetch died. Unlatch to retry
      // on a later message instead of leaving the browser source with no cheermotes for
      // the whole session, but cap it so a persistent outage can't fetch per message.
      if (!Object.keys(m).length && cheerLoadAttempts < 3) cheerLoadStarted = false;
    });
  };

  const finishHistory = () => {
    if (!historyPending) return;
    historyPending = false;
    for (const line of heldLive.splice(0)) {
      // A live line the refill already showed (they overlap at the seam).
      const id = parseIrc(line)?.tags['id'];
      if (id && recentIds.has(id)) continue;
      handleLine(line);
    }
  };

  // After a reconnect, the chat sent while the socket was down comes from the
  // history service (the app refills the same way), ahead of live lines, which
  // wait behind it at most HISTORY_WAIT_MS.
  const refillGap = async () => {
    if (lastSentTs === 0 || historyPending) return;
    const since = lastSentTs;
    historyPending = true;
    const timer = setTimeout(finishHistory, HISTORY_WAIT_MS);
    try {
      const res = await fetch(`${HISTORY_URL}/${encodeURIComponent(login)}?limit=${GAP_LIMIT}`);
      const body = res.ok ? ((await res.json()) as { messages?: unknown }) : null;
      const lines = Array.isArray(body?.messages) ? (body!.messages as unknown[]).filter((l): l is string => typeof l === 'string') : [];
      if (!disposed && historyPending) {
        for (const line of lines) {
          const tags = parseIrc(line)?.tags;
          if (!tags || tags['rm-deleted'] === '1') continue;
          const sent = Number(tags['tmi-sent-ts']);
          if (!(sent >= since) || (tags['id'] && recentIds.has(tags['id']))) continue;
          handleLine(line, true);
        }
      }
    } catch {
      /* the gap stays unfilled; live chat carries on */
    } finally {
      clearTimeout(timer);
      finishHistory();
    }
  };

  const loadHistory = async () => {
    const timer = setTimeout(finishHistory, HISTORY_WAIT_MS);
    try {
      const res = await fetch(`${HISTORY_URL}/${encodeURIComponent(login)}?limit=${HISTORY_LIMIT}`);
      const body = res.ok ? ((await res.json()) as { messages?: unknown }) : null;
      const lines = Array.isArray(body?.messages) ? (body!.messages as unknown[]).filter((l): l is string => typeof l === 'string') : [];
      // Past the wait, live chat has already started; old lines now would land
      // below it, so they are dropped rather than shown out of order.
      if (!disposed && historyPending) {
        // The room id rides on every line; start emotes loading from the first,
        // so history renders with the channel's emotes (the gate holds it while
        // a cold table loads).
        const room = lines.map((l) => parseIrc(l)?.tags['room-id']).find(Boolean);
        if (room) maybeLoadEmotes(room);
        for (const line of lines) handleLine(line, true);
      }
    } catch {
      /* no history is fine; live chat carries on */
    } finally {
      clearTimeout(timer);
      finishHistory();
    }
  };

  const connect = () => {
    if (disposed) return;
    const socket = new WebSocket(IRC_WS);
    ws = socket;
    socket.onopen = () => {
      retry = 1000;
      lastRx = Date.now();
      pingSentAt = 0;
      socket.send('CAP REQ :twitch.tv/tags twitch.tv/commands');
      socket.send('PASS SCHMOOPIIE');
      socket.send(`NICK justinfan${Math.floor(Math.random() * 90000) + 10000}`);
      socket.send(`JOIN #${login}`);
      if (connectedOnce) void refillGap();
      connectedOnce = true;
    };
    socket.onmessage = (ev) => {
      lastRx = Date.now();
      pingSentAt = 0;
      const data = typeof ev.data === 'string' ? ev.data : '';
      for (const line of data.split('\r\n')) {
        if (!line) continue;
        if (line.startsWith('PING')) {
          socket.send(line.replace('PING', 'PONG'));
          continue;
        }
        // Twitch is about to close this socket for maintenance: move now. Only
        // the server's own command counts, never chat text that says it.
        if (RECONNECT_LINE.test(line)) {
          reconnectNow();
          return;
        }
        if (historyPending) {
          heldLive.push(line);
          continue;
        }
        handleLine(line);
      }
    };
    socket.onclose = () => {
      if (disposed || ws !== socket) return;
      const wait = retry;
      retry = Math.min(retry * 2, 30_000);
      setTimeout(connect, wait);
    };
    socket.onerror = () => socket.close();
  };

  /** Drop the current socket without waiting for it to close (a dead network
   *  can take minutes to report it) and connect again. */
  const reconnectNow = () => {
    const dead = ws;
    ws = null;
    if (dead) {
      dead.onopen = null;
      dead.onmessage = null;
      dead.onclose = null;
      dead.onerror = null;
      try {
        dead.close();
      } catch {
        /* already closed */
      }
    }
    if (!disposed) connect();
  };

  const keepalive = setInterval(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (pingSentAt && lastRx < pingSentAt && now - pingSentAt > KEEPALIVE_PONG_MS) {
      reconnectNow();
    } else if (!pingSentAt && now - lastRx > KEEPALIVE_IDLE_MS) {
      pingSentAt = now;
      ws.send('PING :streamnook');
    }
  }, KEEPALIVE_CHECK_MS);

  const handleLine = (line: string, fromHistory = false, released = false) => {
    if (!released && (gateTimer || gated.length > 0)) {
      gated.push([line, fromHistory]);
      return;
    }
    const p = parseIrc(line);
    if (!p) return;
    // Load the channel's 7TV/BTTV/FFZ emotes as soon as the room id is known.
    // ROOMSTATE carries it and arrives right after JOIN (before any chat), so
    // emotes are ready before the first message instead of after it.
    maybeLoadEmotes(p.tags['room-id']);
    maybeLoadCheermotes(p.tags['room-id']);
    maybeListenPoints(p.tags['room-id']);
    if (p.command === 'ROOMSTATE') {
      return;
    }
    // This line started a cold table's load: it waits too, first in line.
    if (gateTimer) {
      gated.unshift([line, fromHistory]);
      return;
    }
    if (p.command === 'PRIVMSG' && !fromHistory && handlers.onCommand && options.commands?.()) {
      const cmd = overlayCommandOf(p.tags, trailing(p.params), PAGE_STARTED_AT);
      if (cmd) {
        // The command is an instruction to the overlay, not chat: never shown.
        handlers.onCommand(cmd);
        return;
      }
    }
    if (p.command === 'PRIVMSG' || p.command === 'USERNOTICE') {
      const msg = p.command === 'PRIVMSG' ? buildPrivmsg(p) : buildUsernotice(p, login);
      settleDisplayName(msg, p.command === 'USERNOTICE');
      // A Shared Chat row sent in a partner channel types that channel's
      // emotes, so it parses with that channel's table (the app does the same).
      const source = p.tags['source-room-id'];
      const partnerRoom = source && source !== p.tags['room-id'] ? source : '';
      const partner = partnerRoom ? partnerTable(partnerRoom) : null;
      if (partner?.held) {
        partner.held.push({ line, fromHistory, id: p.tags['id'] ?? '', userId: p.tags['user-id'] ?? '' });
        return;
      }
      const userId = msg.user_id;
      const sender = msg.username;
      const bits = !!msg.metadata?.bits_amount;
      const withEmotes = (segs: MessageSegment[]) => {
        const table = partner?.loaded ? partner.room.map : emotes;
        return table ? applyEmotes(segs, table, personalEmotesFor(userId), sender) : segs;
      };
      // Bit cheers: turn `Cheer100`-style words into animated cheermote segments
      // (only when the message actually carries bits, so normal chat is untouched).
      const finish = (segs: MessageSegment[]) => {
        const out = withEmotes(segs);
        return cheermotes && bits ? applyCheermotes(out, cheermotes) : out;
      };
      const base = msg.segments ? applyLinks(msg.segments) : undefined;
      if (base) msg.segments = finish(base);
      // History and a reconnect's refill show without an entrance (catch-up).
      if (fromHistory) msg.metadata = { ...msg.metadata, from_backfill: true };
      // Kept for repair before it is handed on: taking the row can install the
      // sender's personal emotes at once (their kept look), and that repair has
      // to reach this row while it is still held, before it shows.
      if (base && base.length > 0 && msg.segments) {
        repairer.keep({
          id: msg.id,
          scopes: partnerRoom ? [partnerRoom, homeRoom] : [homeRoom],
          userId,
          base,
          shown: msg.segments,
          rebuild: finish,
          extend: withEmotes,
        });
      }
      rememberChatter(msg);
      handlers.onMessage(msg);
      rememberShown(msg.id, Number(p.tags['tmi-sent-ts']));
    } else if (p.command === 'CLEARMSG') {
      const id = p.tags['target-msg-id'];
      if (id) {
        dropHeld((h) => h.id === id);
        handlers.onDelete?.(id);
      }
    } else if (p.command === 'CLEARCHAT') {
      const targetUserId = p.tags['target-user-id'];
      dropHeld((h) => !targetUserId || h.userId === targetUserId);
      if (targetUserId) handlers.onClearUser?.(targetUserId);
      else handlers.onClearChat?.();
    }
  };

  /** A row this channel showed, for the next reconnect's refill. */
  const rememberShown = (id: string, sentTs: number) => {
    if (Number.isFinite(sentTs) && sentTs > lastSentTs) lastSentTs = sentTs;
    if (!id) return;
    recentIds.add(id);
    if (recentIds.size > RECENT_IDS_MAX) recentIds.delete(recentIds.values().next().value as string);
  };

  if (historyPending) void loadHistory();
  connect();
  return () => {
    disposed = true;
    clearInterval(keepalive);
    repairer.dispose();
    home?.release();
    for (const p of partners.values()) p.room.release();
    partners.clear();
    stopPoints?.();
    ws?.close();
  };
}

// A redemption as an overlay row. It has no body: the renderer reads the reward off
// the `sn-reward-*` tags (the same ones the app puts on its own redemption rows) and
// draws it as a Channel points event.
function buildRedemption(
  r: Redemption,
  roomId: string,
  login: string,
  known?: { color: string; badges: OverlayBadge[] },
): OverlayBaseMessage {
  const sent = r.redeemedAt ? Date.parse(r.redeemedAt) : NaN;
  const tags: Record<string, string> = {
    'room-id': roomId,
    'user-id': r.userId,
    'display-name': r.userName,
    'sn-reward-title': r.rewardTitle,
  };
  if (r.rewardCost > 0) tags['sn-reward-cost'] = String(r.rewardCost);
  if (r.rewardImage) tags['sn-reward-image'] = r.rewardImage;
  if (r.rewardBackground) tags['sn-reward-bg'] = r.rewardBackground;
  return {
    id: `redeem-${r.id}`,
    user_id: r.userId,
    username: r.userLogin,
    display_name: r.userName,
    color: known?.color ?? resolveNameColor(undefined, r.userId, r.userLogin),
    badges: known?.badges ?? [],
    timestamp: new Date(Number.isFinite(sent) ? sent : Date.now()).toISOString(),
    content: '',
    provider: 'twitch',
    channel: login,
    tags,
    segments: [],
    metadata: {},
  };
}

// ─── IRC parsing ────────────────────────────────────────────────────────────
interface Parsed {
  tags: Record<string, string>;
  prefix: string;
  command: string;
  params: string;
}

function parseIrc(line: string): Parsed | null {
  let rest = line;
  const tags: Record<string, string> = {};
  if (rest.startsWith('@')) {
    const sp = rest.indexOf(' ');
    if (sp < 0) return null;
    const tagstr = rest.slice(1, sp);
    rest = rest.slice(sp + 1);
    for (const pair of tagstr.split(';')) {
      const eq = pair.indexOf('=');
      if (eq >= 0) tags[pair.slice(0, eq)] = unescapeTag(pair.slice(eq + 1));
    }
  }
  let prefix = '';
  if (rest.startsWith(':')) {
    const sp = rest.indexOf(' ');
    if (sp < 0) return null;
    prefix = rest.slice(1, sp);
    rest = rest.slice(sp + 1);
  }
  const sp = rest.indexOf(' ');
  const command = sp < 0 ? rest : rest.slice(0, sp);
  const params = sp < 0 ? '' : rest.slice(sp + 1);
  return { tags, prefix, command, params };
}

function unescapeTag(v: string): string {
  return v.replace(/\\s/g, ' ').replace(/\\:/g, ';').replace(/\\r/g, '\r').replace(/\\n/g, '\n').replace(/\\\\/g, '\\');
}

function nickFromPrefix(prefix: string): string {
  const bang = prefix.indexOf('!');
  return bang < 0 ? prefix : prefix.slice(0, bang);
}

/** The text after the channel in `#<login> :text`. IRC only requires the colon
 *  before a last parameter with spaces: live Twitch always sends it, but the
 *  history service drops it for a one-word message (`#<login> #badges`), and
 *  every such line from recent chat used to draw with no text. */
export function trailing(params: string): string {
  const idx = params.indexOf(' :');
  if (idx >= 0) return params.slice(idx + 2);
  const space = params.indexOf(' ');
  return space < 0 ? '' : params.slice(space + 1);
}

/** The channel a PRIVMSG or USERNOTICE was sent in: `#<login> :text`. */
export function channelOfParams(params: string): string {
  const first = params.split(' ', 1)[0] ?? '';
  return first.startsWith('#') ? first.slice(1).toLowerCase() : '';
}

// Some notices (watch-streak milestones among them) carry the login in
// `display-name` even for a chatter whose own messages carry a localized name,
// so one person showed two names. A notice takes the localized name that
// chatter's messages showed, keyed by user id (a name is never an identity).
// The app settles names the same way in Rust.
const LOCALIZED_NAMES = new Map<string, string>();
const LOCALIZED_NAMES_MAX = 20_000;

function settleDisplayName(msg: OverlayBaseMessage, isNotice: boolean): void {
  if (!msg.user_id) return;
  const display = (msg.display_name || '').trim();
  const sameAsLogin = display.toLowerCase() === msg.username.toLowerCase();
  if (display && !sameAsLogin) {
    if (LOCALIZED_NAMES.size >= LOCALIZED_NAMES_MAX && !LOCALIZED_NAMES.has(msg.user_id)) LOCALIZED_NAMES.clear();
    LOCALIZED_NAMES.set(msg.user_id, display);
  } else if (isNotice) {
    const known = LOCALIZED_NAMES.get(msg.user_id);
    if (known) msg.display_name = known;
  } else if (display) {
    // A chatter's own message is authoritative: they dropped the localized name.
    LOCALIZED_NAMES.delete(msg.user_id);
  }
}

function baseMessage(p: Parsed, login: string, content: string): OverlayBaseMessage {
  const t = p.tags;
  const tsMs = Number(t['tmi-sent-ts']) || Date.now();
  return {
    id: t['id'] || crypto.randomUUID(),
    user_id: t['user-id'] || '',
    username: login,
    display_name: t['display-name'] || login,
    // An empty tag means the chatter never picked a colour; derive the same
    // deterministic default the app's Rust parser uses, or every such chatter
    // renders in the renderer's purple fallback.
    color: resolveNameColor(t['color'], t['user-id'] || '', login),
    badges: parseBadges(t['badges'] || ''),
    timestamp: new Date(tsMs).toISOString(),
    content,
    provider: 'twitch',
    // The channel, not the chatter: a full chat clear and `{channel}` in event
    // templates both read it.
    channel: channelOfParams(p.params),
    tags: t,
    segments: [],
    metadata: {},
  };
}

function buildPrivmsg(p: Parsed): OverlayBaseMessage {
  const user = nickFromPrefix(p.prefix);
  let content = trailing(p.params);
  // /me actions arrive wrapped as `\x01ACTION <text>\x01`. Strip the wrapper and
  // nothing else: Twitch counts emote-tag positions from the text INSIDE it, so
  // they need no shift. Shifting by the 8-char prefix slides every emote 8
  // characters left, over the words before it.
  let emoteOffset = 0;
  let isAction = false;
  if (content.charCodeAt(0) === 1 && content.slice(1, 8) === 'ACTION ') {
    content = content.slice(8, content.charCodeAt(content.length - 1) === 1 ? content.length - 1 : content.length);
    isAction = true;
  }
  // Twitch's composer types a courtesy "@parent " mention into the reply body
  // as ordinary text; the app's Rust tokenizer strips it before segmenting
  // because the renderer draws its own reply mention or context line. Mirror
  // that here, or hosted overlays show the mention twice. Match display name
  // OR login, case-insensitively: the composer inserts the display name,
  // which differs beyond case for localized names.
  if (p.tags['reply-parent-msg-id']) {
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const alts = [p.tags['reply-parent-user-login'] || '', p.tags['reply-parent-display-name'] || '']
      .filter(Boolean)
      .map(esc)
      .join('|');
    if (alts) {
      // Boundary is REQUIRED (\s+ or end), never \s*: with alternation, a
      // short name that prefixes a longer one would otherwise partially strip
      // ("@FooBar hi" with login "foo" must not become "Bar hi"). With the
      // required boundary the short branch fails and the engine falls through
      // to the longer alternative.
      const m = content.match(new RegExp(`^@(?:${alts})(?:\\s+|$)`, 'i'));
      if (m) {
        // Emote-tag positions count the mention as part of the body, so shift
        // them by the stripped codepoint count.
        emoteOffset += Array.from(m[0]).length;
        content = content.slice(m[0].length);
      }
    }
  }
  const msg = baseMessage(p, user, content);
  msg.segments = tokenizeNative(content, p.tags['emotes'] || '', p.tags['gifs'] || '', emoteOffset);
  if (isAction) msg.metadata = { ...msg.metadata, is_action: true };
  const bits = Number(p.tags['bits']);
  if (Number.isFinite(bits) && bits > 0) msg.metadata = { ...msg.metadata, bits_amount: bits };
  const reply = p.tags['reply-parent-msg-id'];
  if (reply) {
    msg.metadata = {
      ...msg.metadata,
      reply_info: {
        parent_msg_id: reply,
        parent_display_name: p.tags['reply-parent-display-name'] || '',
        parent_msg_body: p.tags['reply-parent-msg-body'] || '',
        parent_user_id: p.tags['reply-parent-user-id'] || '',
        parent_user_login: p.tags['reply-parent-user-login'] || '',
      },
    };
  }
  return msg;
}

function buildUsernotice(p: Parsed, login: string): OverlayBaseMessage {
  const user = p.tags['login'] || login;
  const content = trailing(p.params);
  const msg = baseMessage(p, user, content);
  msg.metadata = {
    ...msg.metadata,
    msg_type: p.tags['msg-id'] || undefined,
    system_message: p.tags['system-msg'] || undefined,
  };
  if (content) msg.segments = tokenizeNative(content, p.tags['emotes'] || '', p.tags['gifs'] || '');
  return msg;
}

/** Where a Twitch badge sits in the row, lowest first, in the order Twitch
 *  draws them: role in the channel, then subscription, then what was earned in
 *  the channel, then global badges. The IRC tag carries no order of its own.
 *  Mirrors `twitch_badge_rank` in the app (src-tauri/src/models/chat_layout.rs),
 *  so the overlay and the app draw one chatter's badges the same way. */
function twitchBadgeRank(name: string): number {
  switch (name) {
    case 'broadcaster': return 0;
    case 'staff': case 'admin': case 'global_mod': return 1;
    case 'lead_moderator': case 'moderator': return 2;
    case 'vip': return 3;
    case 'artist-badge': return 4;
    case 'founder': case 'subscriber': return 5;
    case 'bits': case 'bits-leader': case 'bits-charity': case 'sub-gifter': case 'sub-gift-leader':
    case 'predictions': case 'hype-train': case 'moments': case 'clip-champ': return 6;
    default: return 7;
  }
}

function parseBadges(raw: string): OverlayBadge[] {
  if (!raw) return [];
  const out: OverlayBadge[] = [];
  for (const b of raw.split(',')) {
    const slash = b.indexOf('/');
    if (slash < 0) continue;
    out.push({ name: b.slice(0, slash), version: b.slice(slash + 1) });
  }
  // Array.sort is stable, so badges of one rank keep Twitch's order.
  return out.sort((a, b) => twitchBadgeRank(a.name) - twitchBadgeRank(b.name));
}

// Split into Text/Emote/GIF segments using Twitch's `emotes` and `gifs` tags.
// Both index Unicode codepoints with zero-based INCLUSIVE ranges, so iterate by
// codepoint and let them share one sorted position list. A chat GIF's span in
// the text is a bracketed description ("[Y A Y Yes GIF by ...]"); the tag
// carries `<start>-<end>|<gifID>|<gifURL>`, and Twitch requires the URL be used
// exactly as sent. Same shape as the app's Rust tokenizer (irc_service.rs).
function tokenizeNative(content: string, emotesTag: string, gifsTag: string, offset = 0): MessageSegment[] {
  const chars = Array.from(content);
  if (!emotesTag && !gifsTag) return [{ type: 'text', content }];
  const ranges: Array<{ start: number; end: number; id: string; gifUrl?: string }> = [];
  if (emotesTag) {
    for (const group of emotesTag.split('/')) {
      const colon = group.indexOf(':');
      if (colon < 0) continue;
      const id = group.slice(0, colon);
      for (const pos of group.slice(colon + 1).split(',')) {
        const dash = pos.indexOf('-');
        if (dash < 0) continue;
        const start = Number(pos.slice(0, dash)) - offset;
        const end = Number(pos.slice(dash + 1)) - offset;
        if (Number.isFinite(start) && Number.isFinite(end) && start >= 0) ranges.push({ start, end, id });
      }
    }
  }
  if (gifsTag) {
    for (const entry of gifsTag.split(',')) {
      const bar1 = entry.indexOf('|');
      const bar2 = bar1 < 0 ? -1 : entry.indexOf('|', bar1 + 1);
      if (bar1 < 0 || bar2 < 0) continue;
      const pos = entry.slice(0, bar1);
      const id = entry.slice(bar1 + 1, bar2);
      const url = entry.slice(bar2 + 1);
      const dash = pos.indexOf('-');
      if (dash < 0 || !id || !url) continue;
      const start = Number(pos.slice(0, dash)) - offset;
      const end = Number(pos.slice(dash + 1)) - offset;
      if (Number.isFinite(start) && Number.isFinite(end) && start >= 0) ranges.push({ start, end, id, gifUrl: url });
    }
  }
  if (ranges.length === 0) return [{ type: 'text', content }];
  ranges.sort((a, b) => a.start - b.start);
  const segments: MessageSegment[] = [];
  let cursor = 0;
  for (const { start, end, id, gifUrl } of ranges) {
    if (start >= chars.length || end < start) continue;
    if (start > cursor) {
      const text = chars.slice(cursor, start).join('');
      if (text) segments.push({ type: 'text', content: text });
    }
    const endExcl = Math.min(end + 1, chars.length);
    const span = chars.slice(start, endExcl).join('');
    segments.push(
      gifUrl
        ? { type: 'gif', content: span, gif_id: id, gif_url: gifUrl }
        : {
            type: 'emote',
            content: span,
            emote_id: id,
            emote_url: `https://static-cdn.jtvnw.net/emoticons/v2/${id}/default/dark/3.0`,
          },
    );
    cursor = endExcl;
  }
  if (cursor < chars.length) {
    const text = chars.slice(cursor).join('');
    if (text) segments.push({ type: 'text', content: text });
  }
  return segments;
}
